import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface DiagnosticUser {
  id: string;
  full_name: string;
  area: string | null;
  position: string | null;
  age_range: string | null;
  organization_id: string | null;
  organization_name: string | null;
  maturity_level: number | null;
  has_diagnostic: boolean;
}

const MATURITY_LABELS: Record<number, string> = {
  1: 'Nivel 1 - Novato',
  2: 'Nivel 2 - Explorador',
  3: 'Nivel 3 - Practicante',
  4: 'Nivel 4 - Avanzado',
  5: 'Nivel 5 - Experto',
};

export function useDiagnosticMetrics() {
  const profilesQuery = useQuery({
    queryKey: ['admin-diagnostic-metrics-profiles'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select(`
          id, full_name, area, position, age_range, organization_id,
          organizations:organization_id (name)
        `)
        .order('full_name');
      if (error) throw error;
      return data ?? [];
    },
  });

  const diagnosticsQuery = useQuery({
    queryKey: ['admin-diagnostic-metrics-diags'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('participant_diagnostics')
        .select('user_id, maturity_level, completed_at');
      if (error) throw error;
      return data ?? [];
    },
  });

  const orgsQuery = useQuery({
    queryKey: ['admin-metrics-orgs'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('organizations')
        .select('id, name, logo_url')
        .order('name');
      if (error) throw error;
      return data ?? [];
    },
  });

  // Merge profiles with diagnostics
  const users: DiagnosticUser[] = (profilesQuery.data ?? []).map((p: any) => {
    const diag = (diagnosticsQuery.data ?? []).find((d: any) => d.user_id === p.id);
    return {
      id: p.id,
      full_name: p.full_name,
      area: p.area,
      position: p.position,
      age_range: p.age_range,
      organization_id: p.organization_id,
      organization_name: (p.organizations as any)?.name ?? null,
      maturity_level: diag?.maturity_level ?? null,
      has_diagnostic: !!diag?.completed_at,
    };
  });

  return {
    users,
    organizations: orgsQuery.data ?? [],
    isLoading: profilesQuery.isLoading || diagnosticsQuery.isLoading || orgsQuery.isLoading,
    MATURITY_LABELS,
  };
}
